/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible, setModalQuotationVisible } from "@store/slices/appSlice";
import { setMotorbikeInformation, setNewConditionLoan } from "@store/slices/registerSlice";
import { ApiError } from "@interfaces/response";
import { MotorbikeCatalogItem, TaxSystem } from "@interfaces/catalogs";
import { useLazyGetMotorbikeCatalogQuery, useLazyGetTaxSystemsQuery } from "@api/catalogs";
import { usePostConditionMutation, usePutMotorbikeDetailMutation } from "@api/applications";
import CenteredLoader from "@components/CenteredLoader";
import ModalCloseButton from "@components/ModalCloseButton";
import CardInformacionMoto from "@components/CardInformacionMoto";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { calculateAmountMonthly } from "@helpers/funciones";
import * as Sentry from "@sentry/react";

interface CotizadorMotoProps {
  motorbikeCatalog: MotorbikeCatalogItem[];
  taxSystems: TaxSystem | null;
  handleOnClickClose: () => void;
}

const CotizadorMoto = ({ motorbikeCatalog, taxSystems, handleOnClickClose }: CotizadorMotoProps) => {
  // hooks
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // redux hooks
  const loanApplication = useSelector((state: RootState) => state.register.application);
  const motorbikeInfo = useSelector((state: RootState) => state.register.motorbike);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  // api hooks
  const [putMotorbikeDetail] = usePutMotorbikeDetailMutation();
  const [triggerPostCondition] = usePostConditionMutation();

  // state
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleScroll = () => {
    if (!scrollContainerRef.current || motorbikeCatalog.length === 0) return;
    const container = scrollContainerRef.current;
    const pageWidth = container.clientWidth;
    if (pageWidth === 0) return;
    const index = Math.round(container.scrollLeft / pageWidth);
    const safeIndex = Math.max(0, Math.min(index, motorbikeCatalog.length - 1));
    setActiveIndex(safeIndex);
  };

  const scrollToMotoIndex = (index: number) => {
    if (!scrollContainerRef.current || motorbikeCatalog.length === 0) return;
    const container = scrollContainerRef.current;
    const pageWidth = container.clientWidth;
    const safeIndex = Math.max(0, Math.min(index, motorbikeCatalog.length - 1));
    container.scrollTo({ left: safeIndex * pageWidth, behavior: "smooth" });
  };

  const handleOnSelectMotorbike = async (motorbikeItem: MotorbikeCatalogItem) => {
    try {
      if (!motorbikeItem?.data || !motorbikeInfo?.motorbikeDetailId || !taxSystems?.default_term) {
        return false;
      }

      if (!taxSystems?.default_term) {
        throw new Error("No se ha configurado el plazo por defecto");
      }

      setIsSaving(true);

      await putMotorbikeDetail({
        solicitudId: loanApplication.id,
        motorbikeDetailId: motorbikeInfo.motorbikeDetailId,
        body: {
          motorbike_catalog_id: motorbikeItem.id,
          details: {
            model: motorbikeItem.data.model,
            price: motorbikeItem.data.price,
            down_payment: motorbikeItem.data.down_payment,
            daily_payment: motorbikeItem.data.daily_payment,
          },
        },
      }).unwrap();

      await triggerPostCondition({
        loan_application: loanApplication.id,
        loan_amount: motorbikeItem.data.price,
        requested_term: taxSystems?.default_term,
        selected_amount: motorbikeItem.data.price,
        selected_term: taxSystems?.default_term,
        requested_amount: motorbikeItem.data.price,
        user_name: `TOFU - ${accountUser.email}`,
        user_id: String(accountUser.id),
      }).unwrap();

      dispatch(
        setNewConditionLoan({
          approved_amount: loanApplication.approved_amount,
          approved_term: loanApplication.approved_term,
          interest_rate: loanApplication.interest_rate,
          loanAmount: motorbikeItem.data.price,
          loanTerm: taxSystems?.default_term,
          amountMonthly: calculateAmountMonthly(motorbikeItem.data.price, taxSystems?.default_term),
        }),
      );
      dispatch(
        setMotorbikeInformation({
          motorbikeCatalogId: motorbikeItem.id,
        }),
      );
      dispatch(setModalQuotationVisible(false));

      handleOnClickClose();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "CotizadorMoto",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollLeft = 0;
    setActiveIndex(0);
  }, [motorbikeCatalog]);

  return (
    <>
      <DialogTitle sx={{ position: "relative", pr: 6, pt: 2 }}>
        <Typography variant="body1" color={"#002652"} fontWeight={600} textAlign={"center"}>
          Elegir otra moto
        </Typography>
        <ModalCloseButton isSaving={isSaving} handleOnClickClose={handleOnClickClose} />
      </DialogTitle>
      <DialogContent>
        <Box width={isMobileOrTablet ? "100%" : "380px"} my={2}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              aria-label="Moto anterior"
              onClick={() => scrollToMotoIndex(activeIndex - 1)}
              disabled={activeIndex === 0}
              color="primary"
              size="small"
              sx={{ flexShrink: 0 }}
            >
              <KeyboardArrowLeft fontSize="large" />
            </IconButton>

            <Box
              ref={scrollContainerRef}
              onScroll={handleScroll}
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                scrollBehavior: "smooth",
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {motorbikeCatalog.map((moto) => (
                <Box
                  key={moto.id}
                  sx={{
                    flex: "0 0 100%",
                    minWidth: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    display: "flex",
                    justifyContent: "center",
                    scrollSnapAlign: "start",
                    scrollSnapStop: "always",
                    pb: 1,
                  }}
                >
                  <CardInformacionMoto
                    item={moto}
                    onSelect={handleOnSelectMotorbike}
                    size="small"
                    disabled={isSaving}
                    isSelected={motorbikeInfo?.motorbikeCatalogId === moto.id}
                  />
                </Box>
              ))}
            </Box>

            <IconButton
              aria-label="Moto siguiente"
              onClick={() => scrollToMotoIndex(activeIndex + 1)}
              disabled={activeIndex >= motorbikeCatalog.length - 1}
              color="primary"
              size="small"
              sx={{ flexShrink: 0 }}
            >
              <KeyboardArrowRight fontSize="large" />
            </IconButton>
          </Box>

          <Box display="flex" justifyContent="center" gap={1} mt={1.5}>
            {Array.from({ length: motorbikeCatalog.length }).map((_, index) => (
              <Box
                key={index}
                role="button"
                tabIndex={0}
                aria-label={`Ir a moto ${index + 1}`}
                onClick={() => scrollToMotoIndex(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    scrollToMotoIndex(index);
                  }
                }}
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  bgcolor: index === activeIndex ? "primary.light" : "#C9D8F5",
                  transition: "all .2s ease",
                  cursor: "pointer",
                }}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
    </>
  );
};

const QuotationModalMoto = () => {
  // hooks
  const dispatch = useAppDispatch();

  // redux hooks
  const modalState = useSelector((state: RootState) => state.app.modal_quotation);
  const loanApplication = useSelector((state: RootState) => state.register.application);

  // api hooks
  const [getTaxSystems] = useLazyGetTaxSystemsQuery();
  const [getMotorbikeCatalog] = useLazyGetMotorbikeCatalogQuery();

  // state
  const [taxSystems, setTaxSystems] = useState<TaxSystem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [motorbikeCatalog, setMotorbikeCatalog] = useState<MotorbikeCatalogItem[]>([]);

  const handleOnClickClose = () => {
    dispatch(setModalQuotationVisible(false));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (modalState?.visible === false) return;

        setIsLoading(true);

        const [taxSystems, motorbikeCatalog] = await Promise.all([
          getTaxSystems(loanApplication.type).unwrap(),
          getMotorbikeCatalog().unwrap(),
        ]);

        setTaxSystems(taxSystems);
        setMotorbikeCatalog(motorbikeCatalog);
      } catch (error) {
        console.log("Ha ocurrido un error: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [modalState?.visible]);

  if (isLoading) {
    return (
      <Dialog open={modalState?.visible} maxWidth={"sm"}>
        <Box display={"flex"} justifyContent={"center"} alignItems={"center"} width={"280px"} height={"400px"}>
          <CenteredLoader />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog open={modalState?.visible} maxWidth={"sm"}>
      <CotizadorMoto
        motorbikeCatalog={motorbikeCatalog}
        taxSystems={taxSystems}
        handleOnClickClose={handleOnClickClose}
      />
    </Dialog>
  );
};

export default QuotationModalMoto;
